import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, MapPin, Leaf, Building2, BadgeCheck } from "lucide-react";
import { IMAGES, AGENT_REG } from "../data/site";
import { scrollToId } from "../lib/smooth";

const lineAnim = (delay) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] },
});

const FEATURES = [
  { icon: Leaf, title: "Green Spaces", desc: "A healthier, happier lifestyle" },
  { icon: Building2, title: "Modern Residences", desc: "2 & 3 BHK Apartments" },
  { icon: BadgeCheck, title: "Gated Community", desc: "Safe & Secure Living" },
  { icon: MapPin, title: "Prime Location", desc: "Near Newtown Action Area III" },
];

export const Hero = ({ onPrice, onVisit, phone = "+919163841785" }) => {
  return (
    <section id="overview" data-testid="hero-section" className="relative overflow-hidden bg-[#FFFFFF] hero-grain">
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ background: "radial-gradient(58% 48% at 74% 38%, rgba(234,245,214,0.95) 0%, rgba(255,255,255,0) 72%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 lg:pt-40 pb-14 sm:pb-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
              className="overline-tag mb-6" data-testid="hero-overline"
            >
              Forest Hill Realtors presents
            </motion.p>

            <h1 className="font-serif-display text-[#3F6212] text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight" data-testid="hero-headline">
              <span className="mask-line"><motion.span {...lineAnim(0.25)}>Vinayak</motion.span></span>
              <span className="mask-line"><motion.span {...lineAnim(0.38)}>21 <span className="italic font-medium text-[#84CC16]">Acres</span></motion.span></span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9 }}
              className="mt-6 text-[#365314] text-base sm:text-lg max-w-xl font-sub font-semibold"
              data-testid="hero-subheadline"
            >
              2 &amp; 3 BHK Residences Near Newtown Action Area III, Kolkata
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9 }}
              className="mt-3 text-[#4D7C0F] text-sm sm:text-base max-w-xl leading-relaxed"
            >
              Discover thoughtfully planned residences at Hatishala, adjacent to Newtown Action Area III — designed
              around natural light, ventilation, green spaces and practical everyday living.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9 }}
              className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4"
            >
              <button
                data-testid="hero-get-price-btn"
                onClick={onPrice}
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-semibold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300 shadow-[0_18px_36px_-14px_rgba(132,204,22,0.55)]"
              >
                Get Latest Price <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-view-floorplans-btn"
                onClick={() => scrollToId("#floor-plans")}
                className="inline-flex items-center justify-center gap-2 bg-[#FFFFFF] border border-[#9CCB58] text-[#3F6212] font-sub font-semibold text-sm px-7 py-4 rounded-full hover:bg-[#F4FAE8] active:scale-[0.98] transition-all duration-300"
              >
                View Floor Plans <ArrowRight size={16} />
              </button>
              <button
                data-testid="hero-book-visit-btn"
                onClick={onVisit}
                className="inline-flex items-center justify-center gap-2 bg-[#FFFFFF] border border-[#9CCB58] text-[#3F6212] font-sub font-semibold text-sm px-7 py-4 rounded-full hover:bg-[#F4FAE8] active:scale-[0.98] transition-all duration-300"
              >
                <MapPin size={15} className="text-[#65A30D]" /> Book a Site Visit
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25, duration: 1 }}
              className="mt-9 flex items-start gap-3 text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed max-w-xl"
              data-testid="hero-disclosure"
            >
              <ShieldCheck size={16} className="text-[#84CC16] shrink-0 mt-0.5" />
              <p>
                Authorised Channel Partner: <span className="text-[#365314] font-semibold">Forest Hill Realtors</span> · WBRERA Agent Reg. No.{" "}
                <span className="font-mono-spec text-[#365314]">{AGENT_REG}</span>
                <span className="block mt-1 text-[#65A30D]">This is a channel partner showcase, not the official developer website.</span>
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="img-frame rounded-[1.75rem] overflow-hidden">
              <div className="overflow-hidden rounded-[1.25rem]">
                <img
                  src={IMAGES.hero}
                  alt="Official Vinayak 21 Acres aerial render — residential towers with central park at Hatishala, New Town Kolkata"
                  className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
                  loading="eager"
                />
              </div>
            </div>
            <div className="absolute -bottom-5 left-5 sm:left-8 bg-[#FFFFFF] border border-[#E3EFCB] rounded-xl shadow-[0_18px_36px_-18px_rgba(77,124,15,0.35)] px-4 py-3 flex items-center gap-2.5" data-testid="hero-igbc-badge">
              <span className="w-9 h-9 rounded-full bg-[#EAF5D6] flex items-center justify-center shrink-0">
                <Leaf size={15} className="text-[#65A30D]" />
              </span>
              <span className="leading-tight">
                <span className="block text-[12px] font-sub font-bold text-[#365314]">IGBC Platinum Precertified</span>
                <span className="block text-[10px] text-[#65A30D]">Reg. No. GH250539</span>
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-14 sm:mt-16 bg-[#FFFFFF] border border-[#E3EFCB] rounded-2xl shadow-[0_28px_56px_-32px_rgba(77,124,15,0.3)] grid grid-cols-2 lg:grid-cols-4 overflow-hidden"
          data-testid="hero-feature-strip"
        >
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              data-testid={`hero-feature-${i}`}
              className={`flex items-center gap-3.5 p-5 sm:p-6 border-[#E3EFCB] ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} lg:border-b-0 ${i < 3 ? "lg:border-r" : "lg:border-r-0"}`}
            >
              <span className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#DCECC0] flex items-center justify-center shrink-0">
                <f.icon size={18} className="text-[#65A30D]" strokeWidth={1.7} />
              </span>
              <span className="leading-tight min-w-0">
                <span className="block text-[13px] sm:text-sm font-sub font-bold text-[#365314]">{f.title}</span>
                <span className="block text-[11px] sm:text-xs text-[#65A30D] mt-0.5">{f.desc}</span>
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
